<script setup lang="ts">
/**
 * DR-13 · Monthly overage allowance beyond prepaid credit and an optional daily alert.
 *
 * The allowance is accepted and stored whatever the account's standing, so the dialog
 * names the limit work actually stops at right now rather than implying the typed
 * figure is already in force.
 */
import Dialog from '~/components/ui/Dialog.vue'
import Field from '~/components/ui/Field.vue'
import Icon from '~/components/ui/Icon.vue'
import ActionButton from '~/components/ui/ActionButton.vue'
import ParamRow from '~/components/inspector/ParamRow.vue'

const props = defineProps<{ currency: string; credit: string; overage: string; dailyAlert: string; spentThisMonth: string; effectiveLimit: string; overageInEffect: boolean }>()
const emit = defineEmits<{ close: []; submit: [payload: { overage: string; dailyAlert: string }] }>()
const overage = ref(props.overage)
const dailyAlert = ref(props.dailyAlert)
const valid = (value: string, optional = false) => (optional && !value.trim()) || /^\d+(\.\d{1,2})?$/.test(value.trim())
const changed = computed(() => overage.value !== props.overage || dailyAlert.value !== props.dailyAlert)
const ok = computed(() => changed.value && valid(overage.value) && valid(dailyAlert.value, true))
</script>

<template>
  <Dialog title="Spending limits" :meta="currency.toUpperCase()" :width="480" @close="emit('close')">
    <template #icon><Icon name="card" /></template>
    <div class="flex flex-col gap-[var(--measure-7px)] rounded-md border border-border px-3 py-2.5">
      <ParamRow label="Prepaid credit" :value="credit" />
      <ParamRow label="Spent this month" :value="spentThisMonth" />
      <ParamRow label="Limit in effect" :value="effectiveLimit" />
    </div>
    <Field v-model="overage" label="Monthly overage allowance" inputmode="decimal" :hint="overageInEffect ? 'Spend allowed beyond the credit · 0 stops work at the credit' : 'Recorded now · raises the limit once a payment method is on file'" :error="valid(overage) ? undefined : 'Enter an amount'" />
    <Field v-model="dailyAlert" label="Daily alert" inputmode="decimal" hint="Empty turns the alert off" :error="valid(dailyAlert, true) ? undefined : 'Enter an amount or leave empty'" />
    <template #actions>
      <ActionButton label="Cancel" @click="emit('close')" />
      <ActionButton label="Save" variant="primary" :disabled="!ok" @click="emit('submit', { overage: overage.trim(), dailyAlert: dailyAlert.trim() })"><template #icon><Icon name="check" /></template></ActionButton>
    </template>
  </Dialog>
</template>
